import {
    BiButton,
    BiSelect,
    BiSelectOption,
    BiInputField,
    BiLink,
    type BiInputFieldCustomEvent,
    type BiSelectCustomEvent,
} from 'bi-web-components-react';
import { useEffect, useState } from 'react';
import useSWR from 'swr/immutable';
import { onClickGoogleAnalyticsEvent } from '../utils/ga-utils';
import ChatFeedback from './ChatFeedback';
import type { ComponentSelector } from '../types';
import { useTranslation } from 'react-i18next';
import { fetcher } from '@bino/utils/fetcher';

const validateEmail = (email: string) => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
};

export type ContactFormProps = {
    setComponent: (val: ComponentSelector) => void;
};

type TextField = {
    value: string;
    valid: boolean;
};

type DisplayValue = {
    no: string;
    en: string;
};

type DropDownListOption = {
    value: string;
    displayValue: DisplayValue;
};

type MyState = {
    selector: string;
    msg: string;
    category: string;
    showValidation: boolean;
    firstname: TextField;
    lastname: TextField;
    email: TextField;
    wizardStep: number;
    isSaving: boolean;
};

const contactFormDataStep = 1;
const confirmStep = 2;
const errorStep = 3;

export default function ContactForm({ setComponent }: ContactFormProps) {
    const { t, i18n } = useTranslation('chat');
    const { data: categories = [], isLoading: categoriesLoading } = useSWR(
        '/api/sf-case/categories',
        fetcher.get<Array<DropDownListOption>>,
        { keepPreviousData: true },
    );

    const [state, setState] = useState<MyState>({
        selector: 'contactForm',
        msg: '',
        category: '',
        showValidation: false,
        firstname: { value: '', valid: false },
        lastname: { value: '', valid: false },
        email: { value: '', valid: false },
        isSaving: false,
        wizardStep: contactFormDataStep,
    });

    useEffect(() => {
        if (!state.category && categories.length > 0) {
            setState((prev) => ({ ...prev, category: categories[0].value }));
        }
    }, [categories, state]);

    useEffect(() => {
        const chatBackBtn = document.getElementsByClassName('chat-back')[0];
        if (chatBackBtn && chatBackBtn instanceof HTMLButtonElement) {
            chatBackBtn.focus();
        }
    }, []);

    function navigateToFront() {
        setComponent('front');
    }

    async function sendToSf(event: React.MouseEvent<HTMLBiButtonElement, MouseEvent>) {
        event.preventDefault();
        setState((prev) => ({ ...prev, isSaving: true }));
        if (validationOk()) {
            const sfCase = {
                FirstName: state.firstname.value,
                LastName: state.lastname.value,
                Email: state.email.value,
                Category: state.category,
                Message: state.msg,
                PreferredLanguage: i18n.language,
            };

            try {
                await fetcher.post<{ message: string }>('/api/sf-case/contact-us', sfCase);
                setState((prev) => ({ ...prev, isSaving: false, wizardStep: confirmStep }));
            } catch {
                setState((prev) => ({ ...prev, isSaving: false, wizardStep: errorStep }));
            }

            onClickGoogleAnalyticsEvent('Chat', 'Submit contact form', window.location.href);
        } else {
            setState((prev) => ({
                ...prev,
                isSaving: false,
                showValidation: true,
            }));
        }
    }

    function validationOk() {
        setState((prev) => ({ ...prev, showValidation: true }));
        if (categoriesLoading) return false;
        if (!state.firstname.valid) return false;
        if (!state.lastname.valid) return false;
        if (!state.email.valid) return false;
        if (state.category === null || state.category.length <= 0) return false;
        if (state.msg.length === 0) return false;
        return true;
    }

    function setText(msg: string) {
        setState((prev) => ({ ...prev, msg }));
    }

    function setCategory(category: string) {
        setState((prev) => ({ ...prev, category }));
    }

    function setStateProp(propName: string, val: string, valid: boolean) {
        if (propName === 'firstname') setState((prev) => ({ ...prev, firstname: { value: val, valid: valid } }));
        else if (propName === 'lastname') setState((prev) => ({ ...prev, lastname: { value: val, valid: valid } }));
        else if (propName === 'email') setState((prev) => ({ ...prev, email: { value: val, valid: valid } }));
    }

    if (state.wizardStep === contactFormDataStep) {
        return (
            <div className='sfContact'>
                <form className='sfContactForm'>
                    <div className='ContactForm--inputs'>
                        <BiInputField
                            required
                            label={t('firstname')}
                            minLength={1}
                            onChange={(e: BiInputFieldCustomEvent<Event>) =>
                                setStateProp('firstname', e.target.value, e.target.value.length > 0)
                            }
                            name={'given-name'}
                            autocomplete='given-name'
                            errorMessage={t('firstnameValidator')}
                            error={!state.firstname.valid && state.showValidation}
                            value={state.firstname.value}
                        />

                        <BiInputField
                            required
                            label={t('lastname')}
                            minLength={1}
                            onChange={(e: BiInputFieldCustomEvent<Event>) =>
                                setStateProp('lastname', e.target.value, e.target.value.length > 0)
                            }
                            name={'lastname'}
                            autocomplete='family-name'
                            errorMessage={t('lastnameValidator')}
                            error={!state.lastname.valid && state.showValidation}
                            value={state.lastname.value}
                        />

                        <BiInputField
                            required
                            label={t('email')}
                            onChange={(e: BiInputFieldCustomEvent<Event>) =>
                                setStateProp('email', e.target.value, validateEmail(e.target.value))
                            }
                            name={'email'}
                            autocomplete='email'
                            errorMessage={t('mailValidator')}
                            error={!state.email.valid && state.showValidation}
                            value={state.email.value}
                        />

                        <BiSelect
                            errorMessage={t('catValidator')}
                            error={!categoriesLoading && !state.category && state.showValidation}
                            label={t('category')}
                            onChange={(e: BiSelectCustomEvent<Event>) => setCategory(e.target.value)}
                            required
                            value={state.category}
                        >
                            {categories.map((category, idx: number) => (
                                <BiSelectOption
                                    key={`${category.value}-${idx}`}
                                    label={category.displayValue[i18n.language as 'no' | 'en']}
                                    value={category.value}
                                />
                            ))}
                        </BiSelect>

                        <BiInputField
                            required
                            label={t('descPlaceholder')}
                            placeholder={t('descPlaceholder')}
                            minLength={1}
                            onChange={(e: BiInputFieldCustomEvent<Event>) => setText(e.target.value)}
                            name={'message'}
                            errorMessage={t('msgValidator')}
                            error={!state.msg && state.showValidation}
                            value={state.msg}
                            size='long'
                        />
                    </div>

                    <p className='ContactForm--privacy-statement'>
                        {t('gdpr')}
                        <BiLink
                            color='blue'
                            href={i18n.language === 'no' ? '/personvern' : '/privacypolicy'}
                            size='small'
                            state='noline'
                            target='_blank'
                        >
                            {t('gdprPrivacy')}
                        </BiLink>
                    </p>

                    <div className='ContactForm--buttons'>
                        <BiButton
                            id={'contactFormButton'}
                            submitting={state.isSaving}
                            disabled={state.isSaving}
                            type='submit'
                            onClick={sendToSf}
                        >
                            {t('submitButton')}
                        </BiButton>

                        <BiButton variant='outlined' onClick={navigateToFront}>
                            {t('cancel')}
                        </BiButton>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <ChatFeedback
            type={state.wizardStep === confirmStep ? 'confirmation' : 'error'}
            onNavigateBack={navigateToFront}
        />
    );
}
